import React, { useCallback, useMemo, useState } from 'react';
import { FormControl, FormLabel, MenuItem, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';

const defaultStatement = `-Line item(s) [X], [X] and [X] passed TC Chapter review as balloted and will be forwarded to the ISC A&R SC for procedural review.

-Line item(s) [X], [X] and [X] passed TC Chapter review with editorial changes and will be forwarded to the ISC A&R SC for procedural review.

-Line item(s) [X], [X] and [X] passed TC Chapter review with technical changes and with or without editorial changes and will be forwarded to the ISC A&R SC for procedural review. A Ratification Ballot will be issued to verify the technical changes.

-Line item(s) [X], [X] and [X] failed TC Chapter review and will be returned to the TF for rework.

-Line item(s) [X], [X] and [X] failed TC Chapter review and work will be discontinued.`;

const FinalLineActionMotion: React.FunctionComponent = () => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const [statement, setStatement] = useState(defaultStatement);
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `final-line-action-motion`,
      title: 'Action',
      statement: statement,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [discussion, safeBy, safeSecond, statement]);

  const handleClearMotion = useCallback(() => {
    setStatement(defaultStatement);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  return (
    <GridContainer>
      <GridItem>
        <TextField
          label={'Motion'}
          value={statement}
          required
          multiline
          onChange={(event) => {
            setStatement(event.target.value);
          }}
        ></TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="final-line-action-discussion-editor"
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          onClearMotion={handleClearMotion}
          showVotingRuleSelector={false}
          clearFlags={{
            clearDocumentLineItem: true,
            clearReject: true,
            clearTechnicalIssue: true,
            clearComment: true,
            clearIpCheck: true,
          }}
        />
      </GridItem>
    </GridContainer>
  );
};

export default FinalLineActionMotion;
